/**
 * Generic PDF/Print export utility for reports and tables.
 * Uses the browser's built-in print mechanism with styled HTML.
 */
import { getLocaleSettings } from './localeUtils';

function getSettingsForPrint() {
  try {
    const s = JSON.parse(localStorage.getItem('pharmxai_settings') || '{}');
    return {
      businessName: s.business_name || 'DutiLyne',
      address: s.business_address || '',
      phone: s.contact_phone || '',
      email: s.contact_email || '',
    };
  } catch {
    return { businessName: 'DutiLyne', address: '', phone: '', email: '' };
  }
}

/**
 * Generate and open a printable HTML document (works for print / save-as-PDF)
 * @param {Object} opts
 * @param {string} opts.title - Report title
 * @param {string} opts.subtitle - Optional subtitle
 * @param {Array<{label: string, value: string}>} opts.summaryCards - Optional summary metrics
 * @param {Array<string>} opts.headers - Table column headers
 * @param {Array<Array<string|number>>} opts.rows - Table rows
 * @param {boolean} opts.autoPrint - Trigger print dialog automatically (default true)
 */
export function exportToPrintablePDF({ title, subtitle, summaryCards = [], headers = [], rows = [], autoPrint = true }) {
  const biz = getSettingsForPrint();
  const locale = getLocaleSettings();
  const now = new Date().toLocaleDateString();

  const summaryHTML = summaryCards.length > 0 ? `
    <div class="summary-row">
      ${summaryCards.map(c => `
        <div class="summary-card">
          <div class="card-label">${c.label}</div>
          <div class="card-value">${c.value}</div>
        </div>
      `).join('')}
    </div>
  ` : '';

  const tableHTML = headers.length > 0 ? `
    <table>
      <thead>
        <tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr>
      </thead>
      <tbody>
        ${rows.map(row => `<tr>${row.map(cell => `<td>${cell ?? ''}</td>`).join('')}</tr>`).join('')}
      </tbody>
    </table>
  ` : '';

  const html = `
  <!DOCTYPE html>
  <html>
  <head>
    <title>${title} - ${biz.businessName}</title>
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #1a1a2e; padding: 30px; font-size: 12px; }
      .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #20ACA3; padding-bottom: 15px; margin-bottom: 20px; }
      .header-left h1 { font-size: 20px; color: #0f172a; margin-bottom: 4px; }
      .header-left p { font-size: 11px; color: #64748b; }
      .header-right { text-align: right; font-size: 11px; color: #64748b; }
      .header-right strong { color: #0f172a; display: block; font-size: 14px; margin-bottom: 4px; }
      .summary-row { display: flex; gap: 15px; margin-bottom: 20px; flex-wrap: wrap; }
      .summary-card { flex: 1; min-width: 120px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 10px 14px; }
      .card-label { font-size: 10px; text-transform: uppercase; color: #64748b; letter-spacing: 0.5px; }
      .card-value { font-size: 18px; font-weight: 700; color: #0f172a; margin-top: 2px; }
      table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 11px; }
      th { background: #0f172a; color: white; padding: 8px 10px; text-align: left; font-weight: 600; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; }
      td { padding: 7px 10px; border-bottom: 1px solid #e2e8f0; }
      tr:nth-child(even) { background: #f8fafc; }
      tr:hover { background: #f1f5f9; }
      .footer { margin-top: 25px; padding-top: 10px; border-top: 1px solid #e2e8f0; text-align: center; font-size: 10px; color: #94a3b8; }
      @media print { body { padding: 15px; } .no-print { display: none; } }
    </style>
  </head>
  <body>
    <div class="header">
      <div class="header-left">
        <h1>${title}</h1>
        ${subtitle ? `<p>${subtitle}</p>` : ''}
        <p>Generated: ${now}</p>
      </div>
      <div class="header-right">
        <strong>${biz.businessName}</strong>
        ${biz.address ? `<p>${biz.address}</p>` : ''}
        ${biz.phone ? `<p>${biz.phone}</p>` : ''}
        ${biz.email ? `<p>${biz.email}</p>` : ''}
      </div>
    </div>
    ${summaryHTML}
    ${tableHTML}
    <div class="footer">
      ${biz.businessName} &mdash; ${title} &mdash; Printed ${now}
    </div>
  </body>
  </html>
  `;

  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    // Fallback: download as HTML
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/\s+/g, '_')}.html`;
    a.click();
    URL.revokeObjectURL(url);
    return;
  }
  printWindow.document.write(html);
  printWindow.document.close();
  if (autoPrint) {
    printWindow.onload = () => printWindow.print();
  }
}
